import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, UserCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '../features/auth/AuthContext';
import ThemeToggle from './ThemeToggle';
import { COMPANY_LOGO_SRC } from '../constants/branding';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const isLoggedIn = Boolean(user?.isLoggedIn);

  useEffect(() => {
    if (location.state?.user?.isLoggedIn) {
      login(location.state.user);
    }
  }, [location.state, login]);

  useEffect(() => {
    if (user?.role !== 'admin' || !user?.token) return;
    if (location.pathname.startsWith('/admin')) return;
    navigate('/admin', { replace: true });
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/logged-out', { replace: true });
  };

  const showPublicNav = user?.role !== 'admin';

  return (
    <nav className="jet-navbar">
      <div className="jet-navbar__inner">
        <Link to="/" className="jet-brand">
          <img
            src={COMPANY_LOGO_SRC}
            alt="Jet Shipping Cargo"
            className="jet-brand__logo"
            decoding="async"
          />
          <span className="jet-brand__text">
            <span className="jet-brand__title">JET SHIPPING CARGO</span>
            <span className="jet-brand__tag">Global Cargo Solutions</span>
          </span>
        </Link>

        {showPublicNav && (
          <ul className={`jet-nav-links ${isMenuOpen ? 'open' : ''}`}>
            <li>
              <Link to="/" className={isActive('/') ? 'active' : ''}>Home</Link>
            </li>
            <li className="jet-nav-item--dropdown">
              <Link
                to="/services"
                className={`jet-nav-dropdown-trigger ${isActive('/services') || isActive('/pricing') || isActive('/cbm-calculator') || isActive('/sri-lanka-customs-baggage') ? 'active' : ''}`}
              >
                Services
                <ChevronDown className="w-4 h-4 jet-nav-chevron" aria-hidden strokeWidth={2.25} />
              </Link>
              <ul className="jet-nav-dropdown" role="menu">
                <li role="none">
                  <Link to="/services" role="menuitem" onClick={() => setIsMenuOpen(false)}>
                    All services
                  </Link>
                </li>
                <li role="none">
                  <Link to="/pricing" role="menuitem" onClick={() => setIsMenuOpen(false)}>
                    Pricing
                  </Link>
                </li>
                <li role="none">
                  <Link to="/cbm-calculator" role="menuitem" onClick={() => setIsMenuOpen(false)}>
                    CBM calculator
                  </Link>
                </li>
                <li role="none">
                  <Link to="/sri-lanka-customs-baggage" role="menuitem" onClick={() => setIsMenuOpen(false)}>
                    Sri Lanka customs & baggage
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <Link to="/tracking" className={isActive('/tracking') ? 'active' : ''}>Track Shipment</Link>
            </li>
            <li>
              <Link to="/about" className={isActive('/about') ? 'active' : ''}>About Us</Link>
            </li>
            <li>
              <Link to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact Us</Link>
            </li>
          </ul>
        )}

        <div className={`jet-nav-actions ${isMenuOpen ? 'open' : ''}`}>
          <ThemeToggle variant="compact" />
          {isLoggedIn ? (
            <>
              {user?.role !== 'admin' && (
                <Link to="/profile" className="jet-btn-ghost-nav">
                  <UserCircle size={18} />
                  Profile
                </Link>
              )}
              <button type="button" onClick={handleLogout} className="jet-btn-ghost-nav">
                <LogOut size={18} />
                Logout
              </button>
            </>
          ) : (
            showPublicNav && (
              <Link to="/login" className="jet-btn-login">
                <User size={18} strokeWidth={2} aria-hidden />
                Login
              </Link>
            )
          )}
        </div>

        {showPublicNav && (
          <button
            type="button"
            className="jet-nav-toggle"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
